/* Bridle builder controller.
   Cart write is multi-line with shared bundle properties for SupaEasy compatibility. */

(() => {
  'use strict';

  class BridleBuilder extends HTMLElement {
    constructor() {
      super();
      this.state = {
        selectedBrand: null,
        currentStep: 0,
        selections: {},
        stepUnavailable: {},
        // Per-step filter state: { [stepIndex]: { [paramName]: Set<value> } }
        filters: {}
      };
    }

    connectedCallback() {
      // dataset/attributes aren't reliable in the constructor; read config here.
      this.sectionId = this.dataset.sectionId;
      const cfgKey = 'BridleBuilderConfig_' + String(this.sectionId).replace(/-/g, '_');
      this.config = window[cfgKey];
      if (!this.config) return;
      this.cacheRefs();
      this.bindEvents();
      this.updateCta();
      // Record initial DOM order so sorting can be reset back to it.
      this.querySelectorAll('[data-product-card]').forEach((card, index) => {
        card.dataset.originalOrder = String(index);
      });
      // Warm the first component step so the jump from Brand feels instant.
      this.warmStepImages(1);
    }

    // ── Refs ─────────────────────────────────────────────

    cacheRefs() {
      this.refs = {
        announce: this.querySelector('[data-bridle-announce]'),
        stepTitle: this.querySelector('[data-step-title]'),
        sidebar: this.querySelector('[data-sidebar]'),
        sidebarItems: this.querySelector('[data-sidebar-items]'),
        sidebarEmpty: this.querySelector('[data-sidebar-empty]'),
        sidebarSubtotal: this.querySelectorAll('[data-sidebar-subtotal]'),
        addToCart: this.querySelector('[data-add-to-cart]'),
        bottomSheetToggle: this.querySelector('[data-bottom-sheet-toggle]'),
        sidebarCol: this.querySelector('.bridle-builder__sidebar-col'),
        grids: this.querySelectorAll('[data-step-grid]'),
        stepRows: this.querySelectorAll('[data-step-row]'),
        stepRowDots: this.querySelectorAll('[data-step-row-dot]'),
        steps: this.querySelectorAll('[data-step-index]'),
        dots: this.querySelectorAll('[data-step-dot]'),
        prevBtn: this.querySelector('[data-step-prev]'),
        nextBtn: this.querySelector('[data-step-next]'),
        filterDrawer: this.querySelector('[data-filter-drawer]'),
        filterToggle: this.querySelector('[data-filter-toggle]'),
        filterSections: this.querySelector('[data-filter-sections]'),
        filterEmpty: this.querySelector('[data-filter-empty]'),
        filterStepLabel: this.querySelector('[data-filter-step-label]'),
        filterClearAll: this.querySelector('[data-filter-clear-all]'),
        quickview: this.querySelector('[data-quickview]'),
        quickviewImage: this.querySelector('[data-quickview-image]'),
        quickviewTitle: this.querySelector('[data-quickview-title]'),
        quickviewPrice: this.querySelector('[data-quickview-price]'),
        quickviewDescription: this.querySelector('[data-quickview-description]'),
        quickviewVariantWrap: this.querySelector('[data-quickview-variant-wrap]'),
        quickviewAdd: this.querySelector('[data-quickview-add]'),
        quickviewLoading: this.querySelector('[data-quickview-loading]'),
        quickviewBody: this.querySelector('[data-quickview-body]')
      };
      this.quickviewContext = null;
    }

    // ── Events ───────────────────────────────────────────

    bindEvents() {
      // connectedCallback can fire again on re-attach (theme editor); bind listeners once.
      if (this.eventsBound) return;
      this.eventsBound = true;
      this.addEventListener('click', (event) => {
        const quickviewTrigger = event.target.closest('[data-quickview-trigger]');
        if (quickviewTrigger) {
          event.preventDefault();
          this.openQuickview(quickviewTrigger.dataset.quickviewTrigger, quickviewTrigger.closest('[data-product-card]'));
          return;
        }
        const sidebarRemove = event.target.closest('[data-sidebar-remove]');
        if (sidebarRemove) {
          event.preventDefault();
          this.handleSidebarRemove(Number(sidebarRemove.dataset.sidebarRemove));
          return;
        }
        const brandBtn = event.target.closest('[data-brand-select]');
        if (brandBtn) {
          event.preventDefault();
          this.handleBrandSelect(brandBtn.dataset.brandName);
          return;
        }
        const addBtn = event.target.closest('[data-add-to-bundle]');
        if (addBtn) {
          event.preventDefault();
          this.handleAddToBundle(addBtn.closest('[data-product-card]'));
          return;
        }
        const jumpBtn = event.target.closest('[data-step-jump]');
        if (jumpBtn) {
          event.preventDefault();
          this.goToStep(Number(jumpBtn.dataset.stepJump));
          return;
        }
      });

      // Clear the "select a variant" error the moment a real option is chosen.
      this.addEventListener('change', (event) => {
        const variantSelect = event.target.closest('[data-variant-select]');
        if (variantSelect && variantSelect.tagName === 'SELECT' && variantSelect.value) {
          const card = variantSelect.closest('[data-product-card]');
          const errorEl = card && card.querySelector('[data-variant-error]');
          if (errorEl) errorEl.classList.remove('is-visible');
          variantSelect.removeAttribute('aria-invalid');
          this.updateBackorderNote(card);
        }
      });

      if (this.refs.addToCart) {
        this.refs.addToCart.addEventListener('click', () => this.handleAddToCart());
      }
      if (this.refs.bottomSheetToggle) {
        this.refs.bottomSheetToggle.addEventListener('click', () => this.toggleBottomSheet());
      }
      if (this.refs.prevBtn) {
        this.refs.prevBtn.addEventListener('click', () => this.goToStep(Math.max(0, this.state.currentStep - 1)));
      }
      if (this.refs.nextBtn) {
        const maxStep = this.config.steps.length;
        this.refs.nextBtn.addEventListener('click', () => this.goToStep(Math.min(maxStep, this.state.currentStep + 1)));
      }
      if (this.refs.filterToggle) {
        this.refs.filterToggle.addEventListener('click', () => this.openFilterDrawer());
      }
      if (this.refs.filterDrawer) {
        this.refs.filterDrawer.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeFilterDrawer();
        });
        this.refs.filterDrawer.addEventListener('click', (event) => {
          if (event.target.closest('[data-filter-close]')) {
            event.preventDefault();
            this.closeFilterDrawer();
          }
          if (event.target.matches('[data-filter-apply]')) {
            event.preventDefault();
            this.applyStepFilters(this.state.currentStep);
            this.closeFilterDrawer();
          }
          if (event.target.matches('[data-filter-clear-all]')) {
            event.preventDefault();
            this.clearStepFilters(this.state.currentStep);
            this.renderFilterSections(this.state.currentStep);
          }
          const optionInput = event.target.closest('[data-filter-option-input]');
          if (optionInput) {
            this.toggleFilterValue(
              this.state.currentStep,
              optionInput.dataset.paramName,
              optionInput.dataset.value,
              optionInput.checked
            );
          }
        });
      }
    }

    // ── Helpers ──────────────────────────────────────────

    moneyFormat(cents) {
      // Use the shop's money_format so symbol position and separators match the storefront.
      const value = Number(cents) / 100;
      const groupThousands = (numberString) => numberString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const withDecimals = (decimals) => {
        const parts = value.toFixed(decimals).split('.');
        parts[0] = groupThousands(parts[0]);
        return parts.join('.');
      };
      return String(this.config.moneyFormat || '${{amount}}').replace(
        /\{\{\s*(\w+)\s*\}\}/g,
        (match, token) => (token === 'amount_no_decimals' ? withDecimals(0) : withDecimals(2))
      );
    }

    announce(text) {
      if (!this.refs.announce) return;
      this.refs.announce.textContent = '';
      setTimeout(() => { this.refs.announce.textContent = text; }, 50);
    }

    interpolate(template, replacements) {
      let out = String(template || '');
      Object.keys(replacements).forEach((k) => {
        out = out.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), replacements[k]);
      });
      return out;
    }

    // ── Step navigation ──────────────────────────────────

    goToStep(index) {
      this.state.currentStep = index;
      this.refs.stepRows.forEach((row) => {
        const i = Number(row.dataset.stepRow);
        row.classList.toggle('is-active', i === index);
      });
      this.refs.steps.forEach((el) => {
        const i = Number(el.dataset.stepIndex);
        const active = i === index;
        el.classList.toggle('is-active', active);
        if (active) {
          el.setAttribute('aria-current', 'step');
        } else {
          el.removeAttribute('aria-current');
        }
      });
      this.refs.dots.forEach((dot) => {
        const i = Number(dot.dataset.stepDot);
        dot.classList.toggle('bridle-builder__step-dot--filled', i === index);
      });
      const maxIndex = this.config.steps.length;
      const nav = this.querySelector('[data-step-nav]');
      if (nav) nav.style.setProperty('--bb-progress', maxIndex > 0 ? (index / maxIndex).toFixed(4) : '0');
      const progress = this.querySelector('[role="progressbar"]');
      if (progress) progress.setAttribute('aria-valuenow', String(index));
      if (this.refs.prevBtn) this.refs.prevBtn.disabled = index === 0;
      if (this.refs.nextBtn) this.refs.nextBtn.disabled = index >= this.config.steps.length;
      this.updateNextEmphasis();

      if (this.refs.stepTitle) {
        const stepLabel = index === 0
          ? this.config.translations.brandStepTitle
          : this.interpolate(this.config.translations.chooseA, { step: (this.config.steps[index - 1] || {}).label || '' });
        this.refs.stepTitle.textContent = stepLabel;
      }
      const stepName = index === 0
        ? 'Brand'
        : (this.config.steps[index - 1] || {}).label || '';
      this.announce(this.interpolate(this.config.translations.stepChanged, {
        step: String(index + 1),
        label: stepName
      }));

      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollBehavior = reduceMotion ? 'auto' : 'smooth';

      // Scroll the dots row into frame (not the products) so it stays visible on step change.
      const scrollAnchor = this.querySelector('.bridle-builder__step-nav') || this;
      if (scrollAnchor && typeof scrollAnchor.getBoundingClientRect === 'function') {
        const rect = scrollAnchor.getBoundingClientRect();
        // Skip when the anchor is hidden (e.g. the step nav in vertical layout), else top:0 scrolls up.
        if (rect.height > 0) {
          const targetY = Math.max(0, window.scrollY + rect.top - 100);
          window.scrollTo({ top: targetY, behavior: scrollBehavior });
        }
      }

      // Mobile: centre the active marker in the scrollable nav so later steps come into view.
      const stepNav = this.querySelector('[data-step-nav]');
      const activeMarker = this.querySelector('.bridle-builder__step.is-active');
      if (stepNav && activeMarker && stepNav.scrollWidth > stepNav.clientWidth + 1) {
        const navRect = stepNav.getBoundingClientRect();
        const markerRect = activeMarker.getBoundingClientRect();
        const target = stepNav.scrollLeft + (markerRect.left - navRect.left) - (stepNav.clientWidth / 2) + (markerRect.width / 2);
        stepNav.scrollTo({ left: Math.max(0, target), behavior: scrollBehavior });
      }

      // Warm current and next step images so advancing never waits on a lazy fetch.
      this.warmStepImages(index);
      this.warmStepImages(index + 1);
    }

    warmStepImages(index) {
      if (!this.refs.grids) return;
      this.refs.grids.forEach((grid) => {
        if (Number(grid.dataset.stepGrid) !== index) return;
        grid.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    }

    // ── Brand selection ──────────────────────────────────

    async handleBrandSelect(brandName) {
      if (this.state.selectedBrand
          && Object.keys(this.state.selections).length > 0
          && this.state.selectedBrand.toLowerCase() !== brandName.toLowerCase()) {
        if (!confirm(this.config.translations.switchBrandBody)) return;
      }
      this.state.selectedBrand = brandName;
      this.state.selections = {};
      this.state.stepUnavailable = {};
      this.querySelectorAll('[data-product-card][data-selected="true"]').forEach((card) => {
        card.dataset.selected = 'false';
        const cta = card.querySelector('[data-add-to-bundle]');
        if (cta) cta.textContent = this.config.translations.addToBundle;
      });
      this.refs.steps.forEach((el) => {
        el.classList.remove('is-complete', 'is-unavailable');
      });
      this.announce(this.interpolate(this.config.translations.brandSelected, { brand: brandName }));
      this.renderSidebar();
      this.config.steps.forEach((_, i) => this.applyBrandFilter(i + 1));
      this.refreshStepIndicator(0);
      this.updateCta();
      this.goToStep(1);
    }

    // ── Client-side brand filter ─────────────────────────

    applyBrandFilter(stepIndex) {
      const grid = this.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;
      const step = this.config.steps[stepIndex - 1];
      const targetBrand = String(this.state.selectedBrand || '').toLowerCase();
      const cards = grid.querySelectorAll('[data-product-card]');
      let visibleCount = 0;
      cards.forEach((card) => {
        const cardBrand = String(card.dataset.brand || '').toLowerCase();
        const matches = !targetBrand || cardBrand === targetBrand;
        card.style.display = matches ? '' : 'none';
        if (matches) visibleCount += 1;
      });
      const oldEmpty = grid.querySelector('.bridle-builder__empty-step[data-empty-reason="cascade"]');
      if (oldEmpty) oldEmpty.remove();

      this.state.stepUnavailable[stepIndex] = visibleCount === 0 && cards.length > 0;

      if (this.state.stepUnavailable[stepIndex]) {
        const emptyLi = document.createElement('li');
        emptyLi.className = 'bridle-builder__empty-step';
        emptyLi.dataset.emptyReason = 'cascade';
        emptyLi.textContent = this.interpolate(this.config.translations.stepUnavailable, {
          brand: this.state.selectedBrand,
          step: step.label
        });
        grid.appendChild(emptyLi);
      }
      this.refreshStepIndicator(stepIndex);
    }

    refreshStepIndicator(stepIndex) {
      const isComplete = stepIndex === 0
        ? this.state.selectedBrand !== null
        : !!this.state.selections[stepIndex];
      const isUnavailable = !!this.state.stepUnavailable[stepIndex];
      const stepEl = this.querySelector(`[data-step-index="${stepIndex}"]`);
      if (stepEl) {
        stepEl.classList.toggle('is-complete', isComplete);
        stepEl.classList.toggle('is-unavailable', isUnavailable);
      }
      const dotEl = this.querySelector(`[data-step-dot="${stepIndex}"]`);
      if (dotEl) {
        dotEl.classList.toggle('is-complete', isComplete);
        dotEl.classList.toggle('is-unavailable', isUnavailable);
      }
      const rowEl = this.querySelector(`[data-step-row="${stepIndex}"]`);
      if (rowEl) {
        rowEl.classList.toggle('is-complete', isComplete);
        rowEl.classList.toggle('is-unavailable', isUnavailable);
      }
      this.updateNextEmphasis();
    }

    updateNextEmphasis() {
      if (!this.refs.nextBtn) return;
      const mode = this.config.stepAdvanceMode || 'next_cta';
      const idx = this.state.currentStep;
      const isComplete = idx === 0
        ? this.state.selectedBrand !== null
        : !!this.state.selections[idx] || !!this.state.stepUnavailable[idx];
      const shouldEmphasise = mode === 'next_cta' && isComplete && idx < this.config.steps.length;
      this.refs.nextBtn.classList.toggle('is-emphasised', shouldEmphasise);
      const nextLabel = this.refs.nextBtn.querySelector('span');
      if (nextLabel) {
        nextLabel.textContent = shouldEmphasise
          ? (this.config.translations.nextStepCta || 'Next Step')
          : 'Next';
      }
    }

    // ── Add / remove bundle items ────────────────────────

    handleAddToBundle(card) {
      if (!card) return;
      const stepIndex = Number(card.dataset.stepIndex);
      const variantSelect = card.querySelector('[data-variant-select]');
      const selectedOption = variantSelect && variantSelect.selectedOptions
        ? variantSelect.selectedOptions[0]
        : variantSelect;
      if (!selectedOption) return;

      // The placeholder option has no id/price, so adding it would make a £0 line the cart rejects.
      const errorEl = card.querySelector('[data-variant-error]');
      if (variantSelect && variantSelect.tagName === 'SELECT' && !variantSelect.value) {
        if (errorEl) {
          errorEl.classList.add('is-visible');
          this.announce(errorEl.textContent.trim());
        }
        variantSelect.setAttribute('aria-invalid', 'true');
        variantSelect.focus();
        return;
      }
      if (errorEl) errorEl.classList.remove('is-visible');
      if (variantSelect && variantSelect.removeAttribute) variantSelect.removeAttribute('aria-invalid');

      const titleEl = card.querySelector('.bridle-product-card__title');
      const imageEl = card.querySelector('.bridle-card__media img') || card.querySelector('img');
      const ctaEl = card.querySelector('[data-add-to-bundle]');
      const titleText = titleEl ? titleEl.textContent : '';
      const variantId = selectedOption.value;
      const variantPriceCents = Number(selectedOption.dataset.price) || 0;
      const variantTitle = (selectedOption.tagName === 'OPTION')
        ? selectedOption.textContent.trim().replace(/\s+/g, ' ')
        : '';

      const existing = this.state.selections[stepIndex];
      if (existing && existing.cardElement === card) {
        delete this.state.selections[stepIndex];
        card.dataset.selected = 'false';
        if (ctaEl) ctaEl.textContent = this.config.translations.addToBundle;
        this.announce(this.interpolate(this.config.translations.removedFromBundle, { title: titleText }));
      } else {
        if (existing && existing.cardElement) {
          existing.cardElement.dataset.selected = 'false';
          const oldCta = existing.cardElement.querySelector('[data-add-to-bundle]');
          if (oldCta) oldCta.textContent = this.config.translations.addToBundle;
        }
        this.state.selections[stepIndex] = {
          variantId: variantId,
          productHandle: card.dataset.productHandle,
          productId: card.dataset.productId,
          title: titleText,
          price: variantPriceCents,
          image: imageEl ? imageEl.src : '',
          variantTitle: variantTitle,
          backordered: this.isBackordered(selectedOption),
          cardElement: card
        };
        card.dataset.selected = 'true';
        if (ctaEl) ctaEl.textContent = this.config.translations.remove;
        this.announce(this.interpolate(this.config.translations.addedToBundle, { title: titleText }));
      }
      this.renderSidebar();
      this.updateCta();
      this.refreshStepIndicator(stepIndex);

      // Auto-advance on add (not remove): always on pager-less mobile, or in 'auto' mode.
      const isAddition = !!this.state.selections[stepIndex];
      const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
      const mobileAutoAdvance = isMobile && !this.config.mobileShowPager;
      if (isAddition && (this.config.stepAdvanceMode === 'auto' || mobileAutoAdvance)) {
        const maxStep = this.config.steps.length;
        if (stepIndex < maxStep) {
          setTimeout(() => this.goToStep(Math.min(maxStep, stepIndex + 1)), 250);
        }
      }
    }

    handleSidebarRemove(stepIdx) {
      const sel = this.state.selections[stepIdx];
      if (sel && sel.cardElement) {
        sel.cardElement.dataset.selected = 'false';
        const cta = sel.cardElement.querySelector('[data-add-to-bundle]');
        if (cta) cta.textContent = this.config.translations.addToBundle;
      }
      delete this.state.selections[stepIdx];
      this.renderSidebar();
      this.updateCta();
      this.refreshStepIndicator(stepIdx);
    }

    // A variant is backordered when it is addable here but has no stock behind it.
    // The flag is server-rendered per option; never inferred from availability.
    isBackordered(option) {
      return !!(option && option.dataset && option.dataset.backordered === 'true');
    }

    // Quickview variant data comes from product.js, which omits inventory policy,
    // so the flag is read back off the card that opened it.
    isVariantBackorderedOnCard(card, variantId) {
      if (!card || !variantId) return false;
      const match = card.querySelector(`[data-variant-select] [data-variant-id="${variantId}"]`)
        || card.querySelector(`[data-variant-select][value="${variantId}"]`);
      return this.isBackordered(match);
    }

    updateBackorderNote(card) {
      if (!card) return;
      const note = card.querySelector('[data-backorder-note]');
      if (!note) return;
      const variantSelect = card.querySelector('[data-variant-select]');
      const option = variantSelect && variantSelect.selectedOptions
        ? variantSelect.selectedOptions[0]
        : variantSelect;
      const backordered = this.isBackordered(option);
      note.hidden = !backordered;
      if (backordered) this.announce(note.textContent.trim());
    }

    // ── Sidebar render (safe DOM, no innerHTML for data) ─

    renderSidebar() {
      if (!this.refs.sidebarItems) return;
      this.refs.sidebarItems.querySelectorAll('.bridle-sidebar__item').forEach((el) => el.remove());

      const items = Object.entries(this.state.selections);
      if (items.length === 0) {
        if (this.refs.sidebarEmpty) this.refs.sidebarEmpty.removeAttribute('hidden');
      } else {
        if (this.refs.sidebarEmpty) this.refs.sidebarEmpty.setAttribute('hidden', '');
        items.forEach(([stepIdx, sel]) => {
          const li = document.createElement('li');
          li.className = 'bridle-sidebar__item';
          li.dataset.stepIndex = stepIdx;

          const row = document.createElement('div');
          row.className = 'bridle-sidebar__item-row';

          if (sel.image) {
            const img = document.createElement('img');
            img.className = 'bridle-sidebar__item-image';
            img.src = sel.image;
            img.alt = '';
            img.width = 70;
            img.height = 70;
            row.appendChild(img);
          } else {
            const ph = document.createElement('div');
            ph.className = 'bridle-sidebar__item-image';
            row.appendChild(ph);
          }

          const meta = document.createElement('div');
          meta.className = 'bridle-sidebar__item-meta';
          const titleP = document.createElement('p');
          titleP.className = 'bridle-sidebar__item-title';
          const titleText = sel.variantTitle
            ? `${sel.title} (${sel.variantTitle})`
            : sel.title;
          titleP.textContent = titleText;
          meta.appendChild(titleP);
          const priceP = document.createElement('p');
          priceP.className = 'bridle-sidebar__item-price';
          priceP.textContent = this.moneyFormat(sel.price || 0);
          meta.appendChild(priceP);

          // The summary is the last thing seen before add to cart, so the backorder
          // warning has to repeat here, not just on the card the customer has scrolled past.
          if (sel.backordered && this.config.backorderNote) {
            const noteP = document.createElement('p');
            noteP.className = 'bridle-sidebar__item-backorder-note';
            noteP.textContent = this.config.backorderNote;
            meta.appendChild(noteP);
          }

          row.appendChild(meta);

          li.appendChild(row);

          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'bridle-sidebar__item-remove';
          removeBtn.dataset.sidebarRemove = stepIdx;
          removeBtn.setAttribute('aria-label', `Remove ${sel.title} from bundle`);
          const removeText = document.createElement('span');
          removeText.textContent = this.config.translations.remove;
          removeBtn.appendChild(removeText);
          const ns = 'http://www.w3.org/2000/svg';
          const svg = document.createElementNS(ns, 'svg');
          svg.setAttribute('width', '16');
          svg.setAttribute('height', '16');
          svg.setAttribute('viewBox', '0 0 16 16');
          svg.setAttribute('fill', 'none');
          svg.setAttribute('aria-hidden', 'true');
          const p1 = document.createElementNS(ns, 'path');
          p1.setAttribute('d', 'M2 4h12M6 4V2.667A1.333 1.333 0 0 1 7.333 1.333h1.334A1.333 1.333 0 0 1 10 2.667V4m2 0v9.333a1.333 1.333 0 0 1-1.333 1.334H5.333A1.333 1.333 0 0 1 4 13.333V4h8Z');
          p1.setAttribute('stroke', 'currentColor');
          p1.setAttribute('stroke-width', '1.2');
          p1.setAttribute('stroke-linecap', 'round');
          p1.setAttribute('stroke-linejoin', 'round');
          svg.appendChild(p1);
          const p2 = document.createElementNS(ns, 'path');
          p2.setAttribute('d', 'M6.667 7.333v4M9.333 7.333v4');
          p2.setAttribute('stroke', 'currentColor');
          p2.setAttribute('stroke-width', '1.2');
          p2.setAttribute('stroke-linecap', 'round');
          p2.setAttribute('stroke-linejoin', 'round');
          svg.appendChild(p2);
          removeBtn.appendChild(svg);

          li.appendChild(removeBtn);

          this.refs.sidebarItems.appendChild(li);
        });
      }

      const subtotal = items.reduce((acc, [, sel]) => acc + (sel.price || 0), 0);
      this.refs.sidebarSubtotal.forEach((el) => { el.textContent = this.moneyFormat(subtotal); });
    }

    updateCta() {
      if (!this.refs.addToCart) return;
      const allFilled = this.config.steps.every((_, i) => {
        const idx = i + 1;
        // An unavailable step only counts as filled when the merchant chose to skip, not block.
        return this.state.selections[idx] || (this.state.stepUnavailable[idx] && this.config.cascadeFallback === 'skip');
      });
      const ready = this.state.selectedBrand !== null && allFilled;
      this.refs.addToCart.disabled = !ready;
    }

    // ── Quick view modal ─────────────────────────────────

    async openQuickview(handle, sourceCard) {
      if (!this.refs.quickview || !handle) return;
      this.quickviewContext = { handle: handle, sourceCard: sourceCard };
      if (typeof this.refs.quickview.showModal === 'function') {
        this.refs.quickview.showModal();
      } else {
        this.refs.quickview.setAttribute('open', '');
      }
      this.refs.quickview.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      this.showQuickviewLoading(true);
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!res.ok) throw new Error('product fetch failed');
        const product = await res.json();
        this.renderQuickview(product, sourceCard);
      } catch (err) {
        this.closeQuickview();
      } finally {
        this.showQuickviewLoading(false);
      }
    }

    showQuickviewLoading(on) {
      if (this.refs.quickviewLoading) this.refs.quickviewLoading.hidden = !on;
      if (this.refs.quickviewBody) this.refs.quickviewBody.hidden = on;
    }

    closeQuickview() {
      if (!this.refs.quickview) return;
      this.refs.quickview.classList.remove('is-open');
      if (typeof this.refs.quickview.close === 'function') {
        this.refs.quickview.close();
      } else {
        this.refs.quickview.removeAttribute('open');
      }
      document.body.classList.remove('overflow-hidden');
      this.quickviewContext = null;
    }

    renderQuickview(product, sourceCard) {
      if (this.refs.quickviewTitle) this.refs.quickviewTitle.textContent = product.title || '';
      if (this.refs.quickviewPrice) this.refs.quickviewPrice.textContent = this.moneyFormat(product.price || 0);

      let imgSrc = '';
      if (Array.isArray(product.images) && product.images.length > 0) imgSrc = product.images[0];
      else if (sourceCard) imgSrc = sourceCard.querySelector('img')?.src || '';
      if (this.refs.quickviewImage) {
        this.refs.quickviewImage.src = imgSrc;
        this.refs.quickviewImage.alt = product.title || '';
      }

      // Sanitise the description HTML in a detached document before adopting its nodes.
      if (this.refs.quickviewDescription) {
        while (this.refs.quickviewDescription.firstChild) this.refs.quickviewDescription.removeChild(this.refs.quickviewDescription.firstChild);
        const temp = new DOMParser().parseFromString(product.description || '', 'text/html');
        temp.querySelectorAll('script,style,iframe,object,embed').forEach((el) => el.remove());
        temp.querySelectorAll('*').forEach((el) => {
          Array.from(el.attributes).forEach((attr) => {
            const isEventHandler = attr.name.startsWith('on');
            const isScriptUri = /^\s*javascript:/i.test(attr.value);
            if (isEventHandler || isScriptUri) el.removeAttribute(attr.name);
          });
        });
        Array.from(temp.body.childNodes).forEach((node) => {
          this.refs.quickviewDescription.appendChild(document.importNode(node, true));
        });
      }

      if (this.refs.quickviewVariantWrap) {
        while (this.refs.quickviewVariantWrap.firstChild) this.refs.quickviewVariantWrap.removeChild(this.refs.quickviewVariantWrap.firstChild);
        if (Array.isArray(product.variants) && product.variants.length > 1) {
          const select = document.createElement('select');
          select.className = 'bridle-quickview__variant-select';
          select.setAttribute('aria-label', `Size / Colour for ${product.title}`);
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = this.config.translations.variantPickerPlaceholder;
          placeholder.disabled = true;
          placeholder.selected = true;
          select.appendChild(placeholder);
          product.variants.forEach((v) => {
            const opt = document.createElement('option');
            opt.value = String(v.id);
            opt.dataset.price = String(v.price);
            opt.dataset.available = String(v.available);
            if (v.available) {
              opt.textContent = v.title;
            } else {
              opt.disabled = true;
              opt.textContent = `${v.title} - ${this.config.translations.soldOut}`;
            }
            select.appendChild(opt);
          });
          this.refs.quickviewVariantWrap.appendChild(select);
        } else if (Array.isArray(product.variants) && product.variants.length === 1) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.dataset.singleVariant = '';
          hidden.value = String(product.variants[0].id);
          hidden.dataset.price = String(product.variants[0].price);
          this.refs.quickviewVariantWrap.appendChild(hidden);
        }
      }

      this.quickviewContext.product = product;

      if (this.refs.quickviewAdd && !this.quickviewAddBound) {
        this.refs.quickviewAdd.addEventListener('click', () => this.handleQuickviewAdd());
        this.quickviewAddBound = true;
      }

      if (!this.quickviewCloseBound) {
        this.refs.quickview.addEventListener('click', (event) => {
          if (event.target.matches('[data-quickview-close]')) {
            event.preventDefault();
            this.closeQuickview();
          }
        });
        this.refs.quickview.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeQuickview();
        });
        this.quickviewCloseBound = true;
      }
    }

    handleQuickviewAdd() {
      const ctx = this.quickviewContext;
      if (!ctx || !ctx.product) return;
      const stepIdx = ctx.sourceCard ? Number(ctx.sourceCard.dataset.stepIndex) : this.state.currentStep;
      const variantSelect = this.refs.quickviewVariantWrap.querySelector('select, [data-single-variant]');
      let variantId;
      let priceCents = 0;
      let variantTitle = '';
      if (variantSelect && variantSelect.tagName === 'SELECT') {
        const opt = variantSelect.selectedOptions && variantSelect.selectedOptions[0];
        if (!opt || !opt.value) {
          variantSelect.setAttribute('aria-invalid', 'true');
          variantSelect.focus();
          return;
        }
        variantSelect.removeAttribute('aria-invalid');
        variantId = opt.value;
        priceCents = Number(opt.dataset.price) || 0;
        variantTitle = opt.textContent;
      } else if (variantSelect) {
        variantId = variantSelect.value;
        priceCents = Number(variantSelect.dataset.price) || 0;
      }
      if (!variantId) return;

      const titleText = ctx.product.title || '';
      const image = (Array.isArray(ctx.product.images) && ctx.product.images[0]) || '';
      this.state.selections[stepIdx] = {
        variantId: variantId,
        productHandle: ctx.product.handle,
        productId: String(ctx.product.id),
        title: titleText,
        price: priceCents,
        image: image,
        variantTitle: variantTitle,
        backordered: this.isVariantBackorderedOnCard(ctx.sourceCard, variantId),
        cardElement: ctx.sourceCard || null
      };
      if (ctx.sourceCard) {
        ctx.sourceCard.dataset.selected = 'true';
        const cta = ctx.sourceCard.querySelector('[data-add-to-bundle]');
        if (cta) cta.textContent = this.config.translations.remove;
      }
      this.announce(this.interpolate(this.config.translations.addedToBundle, { title: titleText }));
      this.renderSidebar();
      this.updateCta();
      this.refreshStepIndicator(stepIdx);
      this.closeQuickview();
    }

    // ── Filter & Sort drawer ─────────────────────────────

    openFilterDrawer() {
      if (!this.refs.filterDrawer) return;
      this.renderFilterSections(this.state.currentStep);
      if (typeof this.refs.filterDrawer.showModal === 'function') {
        this.refs.filterDrawer.showModal();
      } else {
        this.refs.filterDrawer.setAttribute('open', '');
      }
      this.refs.filterDrawer.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
    }

    closeFilterDrawer() {
      if (!this.refs.filterDrawer) return;
      this.refs.filterDrawer.classList.remove('is-open');
      if (typeof this.refs.filterDrawer.close === 'function') {
        this.refs.filterDrawer.close();
      } else {
        this.refs.filterDrawer.removeAttribute('open');
      }
      document.body.classList.remove('overflow-hidden');
    }

    renderFilterSections(stepIndex) {
      if (!this.refs.filterSections) return;
      const step = stepIndex === 0 ? null : this.config.steps[stepIndex - 1];

      if (this.refs.filterStepLabel) {
        this.refs.filterStepLabel.textContent = step ? step.label : 'Brand';
      }

      this.refs.filterSections.querySelectorAll('.bridle-drawer__section').forEach((el) => el.remove());

      const sortSection = this.buildSortSection(stepIndex);
      this.refs.filterSections.appendChild(sortSection);

      this.deriveStepFacets(stepIndex).forEach((facet) => {
        const section = this.buildFilterSection(stepIndex, facet);
        if (section) this.refs.filterSections.appendChild(section);
      });

      if (this.refs.filterClearAll) this.refs.filterClearAll.hidden = !this.hasActiveFilters(stepIndex);
    }

    // Facets are derived from the step's brand-matched cards so they stay live and brand-aware.
    deriveStepFacets(stepIndex) {
      const grid = this.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return [];
      const targetBrand = String(this.state.selectedBrand || '').toLowerCase();
      const facets = new Map();
      grid.querySelectorAll('[data-product-card]').forEach((card) => {
        const cardBrand = String(card.dataset.brand || '').toLowerCase();
        if (targetBrand && cardBrand !== targetBrand) return;
        this.cardOptions(card).forEach((option) => {
          if (!option || !option.handle) return;
          if (!facets.has(option.handle)) facets.set(option.handle, { label: option.label || option.handle, values: new Map() });
          const bucket = facets.get(option.handle).values;
          (option.values || []).forEach((value) => bucket.set(value, (bucket.get(value) || 0) + 1));
        });
      });
      return Array.from(facets.entries()).map(([handle, facet]) => ({
        type: 'list',
        label: facet.label,
        paramName: handle,
        values: Array.from(facet.values.entries()).map(([value, count]) => ({ value: value, label: value, count: count }))
      }));
    }

    cardOptions(card) {
      try {
        return JSON.parse(card.dataset.filterOptions || '[]');
      } catch (err) {
        return [];
      }
    }

    buildSortSection(stepIndex) {
      const wrap = document.createElement('section');
      wrap.className = 'bridle-drawer__section';

      const heading = document.createElement('h3');
      heading.className = 'bridle-drawer__section-title';
      heading.textContent = this.config.translations.sort;
      wrap.appendChild(heading);

      const select = document.createElement('select');
      select.className = 'bridle-drawer__sort-select';
      select.dataset.filterSort = stepIndex;
      const opts = [
        { v: 'default', l: 'Default' },
        { v: 'price-asc', l: 'Price (low to high)' },
        { v: 'price-desc', l: 'Price (high to low)' },
        { v: 'name-asc', l: 'Name (A to Z)' }
      ];
      const currentSort = this.getFilterState(stepIndex).__sort || 'default';
      opts.forEach((o) => {
        const opt = document.createElement('option');
        opt.value = o.v;
        opt.textContent = o.l;
        if (o.v === currentSort) opt.selected = true;
        select.appendChild(opt);
      });
      select.addEventListener('change', () => {
        const s = this.getFilterState(stepIndex);
        s.__sort = select.value;
      });
      wrap.appendChild(select);
      return wrap;
    }

    buildFilterSection(stepIndex, filter) {
      if (filter.type !== 'list') return null;
      const wrap = document.createElement('section');
      wrap.className = 'bridle-drawer__section';

      const heading = document.createElement('h3');
      heading.className = 'bridle-drawer__section-title';
      heading.textContent = filter.label;
      wrap.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'bridle-drawer__option-list';
      const state = this.getFilterState(stepIndex);
      const activeSet = state[filter.paramName] || new Set();

      (filter.values || []).forEach((v) => {
        const li = document.createElement('li');
        li.className = 'bridle-drawer__option';
        const id = `bridle-filter-${stepIndex}-${filter.paramName}-${v.value}`.replace(/[^a-z0-9-_]/gi, '_');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.dataset.filterOptionInput = '';
        input.dataset.paramName = filter.paramName;
        input.dataset.value = v.value;
        if (activeSet.has(v.value)) input.checked = true;
        const label = document.createElement('label');
        label.htmlFor = id;
        label.textContent = `${v.label} (${v.count})`;
        li.appendChild(input);
        li.appendChild(label);
        list.appendChild(li);
      });

      wrap.appendChild(list);
      return wrap;
    }

    getFilterState(stepIndex) {
      if (!this.state.filters[stepIndex]) this.state.filters[stepIndex] = {};
      return this.state.filters[stepIndex];
    }

    toggleFilterValue(stepIndex, paramName, value, isOn) {
      const state = this.getFilterState(stepIndex);
      if (!state[paramName]) state[paramName] = new Set();
      if (isOn) state[paramName].add(value);
      else state[paramName].delete(value);
      if (state[paramName].size === 0) delete state[paramName];
    }

    clearStepFilters(stepIndex) {
      const state = this.getFilterState(stepIndex);
      Object.keys(state).forEach((k) => { if (k !== '__sort') delete state[k]; });
    }

    hasActiveFilters(stepIndex) {
      const state = this.getFilterState(stepIndex);
      return Object.keys(state).some((k) => k !== '__sort' && state[k] && state[k].size > 0);
    }

    applyStepFilters(stepIndex) {
      const grid = this.querySelector(`[data-step-grid="${stepIndex}"]`);
      if (!grid) return;
      const state = this.getFilterState(stepIndex);
      const targetBrand = String(this.state.selectedBrand || '').toLowerCase();
      const cards = Array.from(grid.querySelectorAll('[data-product-card]'));

      cards.forEach((card) => {
        const cardBrand = String(card.dataset.brand || '').toLowerCase();
        const brandMatch = !targetBrand || cardBrand === targetBrand;
        if (!brandMatch) {
          card.style.display = 'none';
          return;
        }
        const options = this.cardOptions(card);
        let optionMatch = true;
        Object.keys(state).forEach((paramName) => {
          if (paramName === '__sort') return;
          const activeSet = state[paramName];
          if (!activeSet || activeSet.size === 0) return;
          const option = options.find((o) => o.handle === paramName);
          const cardValues = (option && option.values) || [];
          const anyMatch = Array.from(activeSet).some((v) => cardValues.includes(v));
          if (!anyMatch) optionMatch = false;
        });
        card.style.display = optionMatch ? '' : 'none';
      });

      // Sort every card (not just visible) so hidden cards keep a coherent order and
      // 'default' restores the original DOM order via data-originalOrder.
      const sort = state.__sort || 'default';
      cards.sort((a, b) => {
        if (sort === 'price-asc') return Number(a.dataset.price || 0) - Number(b.dataset.price || 0);
        if (sort === 'price-desc') return Number(b.dataset.price || 0) - Number(a.dataset.price || 0);
        if (sort === 'name-asc') return (a.dataset.productHandle || '').localeCompare(b.dataset.productHandle || '');
        return Number(a.dataset.originalOrder || 0) - Number(b.dataset.originalOrder || 0);
      });
      cards.forEach((c) => grid.appendChild(c));

      const visibleCount = cards.filter((c) => c.style.display !== 'none').length;
      this.state.stepUnavailable[stepIndex] = visibleCount === 0 && cards.length > 0;
      this.refreshStepIndicator(stepIndex);
      this.updateCta();
    }

    // ── Bottom sheet (mobile) ────────────────────────────

    toggleBottomSheet() {
      const col = this.refs.sidebarCol;
      if (!col) return;
      const next = col.dataset.expanded !== 'true';
      col.dataset.expanded = String(next);
      this.refs.bottomSheetToggle.setAttribute('aria-expanded', String(next));
    }

    // ── Cart write ───────────────────────────────────────

    async handleAddToCart() {
      if (this.refs.addToCart.disabled) return;
      this.refs.addToCart.disabled = true;
      this.refs.addToCart.dataset.loading = 'true';
      const bundleId = (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID())
        || ('bridle-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8));
      // One items array is all-or-nothing, so a mid-bundle failure never leaves a partial bridle.
      const items = Object.entries(this.state.selections).map(([stepIdx, sel]) => {
        const step = this.config.steps[Number(stepIdx) - 1];
        const properties = {};
        properties[this.config.bundleTagKey] = this.config.bundleTagValue;
        properties['_bridle_bundle_id'] = bundleId;
        properties['_bridle_brand'] = this.state.selectedBrand;
        properties['_bridle_step'] = step ? step.label : '';
        return { id: Number(sel.variantId), quantity: 1, properties: properties };
      });
      // Request the drawer's section HTML in the add; renderContents needs response.sections.
      const cartDrawer = document.querySelector('cart-drawer');
      const body = { items: items };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      try {
        const res = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        const response = await res.json();
        if (!res.ok || response.status) {
          const cartError = new Error(response.description || 'cart/add.js failed');
          // Shopify's own description (e.g. stock limits) is customer-facing; surface it.
          cartError.userMessage = response.description;
          throw cartError;
        }
        // renderContents re-renders and opens the drawer; publish keeps the cart count in sync.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bridle-builder', cartData: response });
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(response);
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          window.location.href = this.config.cartUrl;
        }

        this.announce(this.config.translations.cartAdded);
      } catch (err) {
        alert(err.userMessage || this.config.translations.cartError);
      } finally {
        this.refs.addToCart.disabled = false;
        delete this.refs.addToCart.dataset.loading;
        this.updateCta();
      }
    }
  }

  if (!customElements.get('bridle-builder')) {
    customElements.define('bridle-builder', BridleBuilder);
  }
})();
